import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import {
  useLazyGetMeetingByIdQuery,
  useUpdateMeetingMutation,
  useLazyGetLeadsQuery,
  useLazyGetLeadsStatusQuery,
  useLazyGetUsersByRoleQuery,
} from '../../store/api';
import { extractList } from '../../utils/response';
import { getValidationErrorDetails } from '../../utils/errorHandler';
import { toast } from 'react-toastify';
import { useLazySelectOptions } from '../../hooks/useLazySelectOptions';
import { CALENDAR_SCHEDULE_PATH, MEETING_VIEW_PATH } from '../../navigations/path';
import FutureDatetimeTextField from '../../components/forms/FutureDatetimeTextField';
import {
  nowDatetimeLocal,
  toDatetimeLocalFromApi,
  clampDatetimeLocal,
  getMinEndDatetimeLocal,
} from '../../utils/datetimeLocal';

const TIME_ZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'EST', label: 'EST' },
  { value: 'PST', label: 'PST' },
  { value: 'CST', label: 'CST' },
  { value: 'GMT', label: 'GMT' },
];

const idEq = (a, b) => a != null && b != null && (a === b || String(a) === String(b));

const mergeLeadOption = (prev, lead) => {
  if (!lead?.id) return prev;
  if (prev.some((l) => idEq(l.id, lead.id))) return prev;
  return [
    ...prev,
    {
      id: lead.id,
      name: lead.name || lead.dba_name || lead.email || 'Lead',
      email: lead.email,
    },
  ];
};

const mergeStatusOption = (prev, status) => {
  if (!status?.id) return prev;
  if (prev.some((s) => idEq(s.id, status.id))) return prev;
  return [...prev, status];
};

const mergeAssigneeOptions = (prev, attendees) => {
  let next = prev;
  for (const a of attendees || []) {
    const aid = a.id ?? a.uuid;
    if (!aid) continue;
    if (!next.some((p) => idEq(p.id, aid))) {
      next = [...next, { id: aid, name: a.name || a.username || 'User' }];
    }
  }
  return next;
};

const EditMeeting = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fetchMeetingById] = useLazyGetMeetingByIdQuery();
  const [updateMeeting] = useUpdateMeetingMutation();
  const [fetchLeads] = useLazyGetLeadsQuery();
  const [fetchLeadsStatus] = useLazyGetLeadsStatusQuery();
  const [fetchUsersByRole] = useLazyGetUsersByRoleQuery();
  const [currentMeeting, setCurrentMeeting] = useState(null);
  const [meetingLoading, setMeetingLoading] = useState(true);

  const [formData, setFormData] = useState({
    lead_id: '',
    status_id: '',
    assignee_ids: [],
    time_zone: 'UTC',
    start_date_time: '',
    end_date_time: '',
    description: '',
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [minScheduleDatetime] = useState(() => nowDatetimeLocal());
  const [formHydrated, setFormHydrated] = useState(false);

  const leadSelect = useLazySelectOptions(fetchLeads, {
    mapResult: (res) => {
      const leadsList = extractList(res);
      return (Array.isArray(leadsList) ? leadsList : []).map((l) => ({
        id: l.id ?? l.uuid,
        name: l.name || l.dba_name || l.email || 'Unknown',
        email: l.email,
      }));
    },
  });

  const statusSelect = useLazySelectOptions(fetchLeadsStatus, {
    args: { 'conditions[model]': 'Meeting' },
    mapResult: (res) => {
      const statusList = extractList(res);
      return Array.isArray(statusList) ? statusList : [];
    },
  });

  const assigneeSelect = useLazySelectOptions(fetchUsersByRole, {
    args: 'Closer',
    mapResult: (res) => {
      const usersList = Array.isArray(res?.data) ? res.data : extractList(res);
      return (Array.isArray(usersList) ? usersList : []).map((u) => ({
        id: u.uuid ?? u.id,
        name: u.name || u.username || 'Unknown',
      }));
    },
  });

  const handleLeadOpen = useCallback(() => {
    leadSelect.load().catch(() => toast.error('Failed to load leads'));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [leadSelect.load]);

  const handleStatusOpen = useCallback(() => {
    statusSelect.load().catch(() => toast.error('Failed to load statuses'));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [statusSelect.load]);

  const handleAssigneeOpen = useCallback(() => {
    assigneeSelect.load().catch(() => toast.error('Failed to load assignees'));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [assigneeSelect.load]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        setMeetingLoading(true);
        const res = await fetchMeetingById(id).unwrap();
        const meeting = res?.data ?? res;
        if (!cancelled) setCurrentMeeting(meeting);
      } catch {
        if (!cancelled) setCurrentMeeting(null);
      } finally {
        if (!cancelled) setMeetingLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      setCurrentMeeting(null);
      setFormHydrated(false);
    };
  }, [id, fetchMeetingById]);

  useEffect(() => {
    if (!currentMeeting || formHydrated) return;
    const m = currentMeeting;
    const attendees = m.attendees || [];
    const assigneeIds = attendees.map((a) => a.id ?? a.uuid).filter(Boolean);

    const lead = m.lead;
    if (lead?.id) {
      leadSelect.setOptions((prev) => mergeLeadOption(prev, lead));
    }
    if (m.status?.id) {
      statusSelect.setOptions((prev) => mergeStatusOption(prev, m.status));
    }
    assigneeSelect.setOptions((prev) => mergeAssigneeOptions(prev, attendees));

    const start = clampDatetimeLocal(toDatetimeLocalFromApi(m.start_date_time));
    const end = clampDatetimeLocal(
      toDatetimeLocalFromApi(m.end_date_time),
      getMinEndDatetimeLocal(start)
    );
    setFormData({
      lead_id: lead?.id ?? lead?.uuid ?? '',
      status_id: m.status?.id ?? m.status?.uuid ?? '',
      assignee_ids: assigneeIds,
      time_zone: m.time_zone || 'UTC',
      start_date_time: start,
      end_date_time: end,
      description: m.description || '',
    });
    setFormHydrated(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [currentMeeting, formHydrated, leadSelect.setOptions, statusSelect.setOptions, assigneeSelect.setOptions]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!formData.lead_id) next.lead_id = 'Lead is required';
    if (!formData.status_id) next.status_id = 'Status is required';
    if (!formData.assignee_ids?.length) next.assignee_ids = 'At least one assignee is required';
    if (!formData.start_date_time) next.start_date_time = 'Start date & time is required';
    if (!formData.end_date_time) next.end_date_time = 'End date & time is required';
    if (formData.start_date_time && formData.end_date_time) {
      const start = new Date(formData.start_date_time).getTime();
      const end = new Date(formData.end_date_time).getTime();
      if (end <= start) next.end_date_time = 'End must be after start';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || !id) return;

    setSubmitting(true);
    try {
      const payload = {
        lead_id: formData.lead_id,
        status_id: formData.status_id,
        assignee_ids: formData.assignee_ids,
        time_zone: formData.time_zone,
        start_date_time: formData.start_date_time,
        end_date_time: formData.end_date_time,
        description: formData.description?.trim() || '',
      };

      await updateMeeting({ id, ...payload }).unwrap();
      toast.success('Meeting updated successfully');
      navigate(`${MEETING_VIEW_PATH}/${id}`);
    } catch (err) {
      const { message, errors: serverErrors } = getValidationErrorDetails(err);
      toast.error(message || err.message || 'Failed to update meeting');
      if (Object.keys(serverErrors).length > 0) {
        const { assignees, ...rest } = serverErrors;
        setErrors((prev) => ({
          ...prev,
          ...rest,
          ...(assignees ? { assignee_ids: assignees } : {}),
        }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const { options: leadOptions } = leadSelect;
  const { options: statusOptions } = statusSelect;
  const { options: assigneeOptions } = assigneeSelect;

  const selectedLead =
    leadOptions.find((l) => idEq(l.id, formData.lead_id)) || null;
  const selectedAssignees =
    assigneeOptions.filter((a) => formData.assignee_ids?.some((aid) => idEq(aid, a.id))) || [];

  if (meetingLoading && !currentMeeting) {
    return (
      <PageLayout>
        <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
      </PageLayout>
    );
  }

  if (!currentMeeting && !meetingLoading) {
    return (
      <PageLayout>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Typography color="text.secondary">Meeting not found.</Typography>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(CALENDAR_SCHEDULE_PATH)} sx={{ mt: 2 }}>
            Back to Calendar
          </Button>
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(CALENDAR_SCHEDULE_PATH)} sx={{ mb: 2 }}>
          Back to Calendar
        </Button>

        <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
          Edit Meeting
        </Typography>

        <Card variant="outlined">
          <CardContent>
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={leadOptions}
                    loading={leadSelect.loading}
                    onOpen={handleLeadOpen}
                    getOptionLabel={(opt) => (opt ? `${opt.name}${opt.email ? ` (${opt.email})` : ''}` : '')}
                    value={selectedLead}
                    onChange={(_, value) => handleChange('lead_id', value?.id ?? '')}
                    isOptionEqualToValue={(opt, val) => idEq(opt?.id, val?.id)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Lead"
                        required
                        error={!!errors.lead_id}
                        helperText={errors.lead_id}
                        placeholder="Search and select lead"
                      />
                    )}
                    noOptionsText="No leads found"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={statusOptions}
                    loading={statusSelect.loading}
                    onOpen={handleStatusOpen}
                    getOptionLabel={(option) => option.name || ""}
                    value={statusOptions.find((s) => idEq(s.id, formData.status_id)) || null}
                    onChange={(event, newValue) => {
                      handleChange("status_id", newValue ? newValue.id : "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Meeting Status"
                        required
                        error={!!errors.status_id}
                        helperText={errors.status_id}
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    multiple
                    options={assigneeOptions}
                    loading={assigneeSelect.loading}
                    onOpen={handleAssigneeOpen}
                    getOptionLabel={(opt) => opt?.name ?? ''}
                    value={selectedAssignees}
                    onChange={(_, value) => handleChange('assignee_ids', value?.map((v) => v.id) ?? [])}
                    isOptionEqualToValue={(opt, val) => idEq(opt?.id, val?.id)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Assignees"
                        error={!!errors.assignee_ids}
                        helperText={errors.assignee_ids}
                        placeholder="Select assignees"
                      />
                    )}
                    noOptionsText="No users found"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={TIME_ZONES}
                    getOptionLabel={(option) => option.label || ""}
                    value={TIME_ZONES.find((tz) => tz.value === formData.time_zone) || null}
                    onChange={(event, newValue) => {
                      handleChange("time_zone", newValue ? newValue.value : "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Time Zone"
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} />

                <Grid item xs={12} sm={6}>
                  <FutureDatetimeTextField
                    fullWidth
                    label="Start Date & Time"
                    value={formData.start_date_time}
                    onChange={(v) => handleChange('start_date_time', v)}
                    minDatetime={minScheduleDatetime}
                    error={!!errors.start_date_time}
                    helperText={errors.start_date_time || undefined}
                    showFutureHelper={!errors.start_date_time}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FutureDatetimeTextField
                    fullWidth
                    label="End Date & Time"
                    value={formData.end_date_time}
                    onChange={(v) => handleChange('end_date_time', v)}
                    minDatetime={getMinEndDatetimeLocal(formData.start_date_time)}
                    error={!!errors.end_date_time}
                    helperText={errors.end_date_time || undefined}
                    showFutureHelper={!errors.end_date_time}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Meeting agenda or notes…"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
                      disabled={submitting}
                    >
                      {submitting ? 'Saving…' : 'Update Meeting'}
                    </Button>
                    <Button variant="outlined" onClick={() => navigate(`${MEETING_VIEW_PATH}/${id}`)}>
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default EditMeeting;
